import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, LinearProgress, Paper, Radio, RadioGroup, Stack, Typography,
} from "@mui/material";
import i18next from "i18next";

export function MyDataUpdateDialog({ state, onClose, onCheck, onStart, onCancel, onDecisionChange, onRequestFoundations, onCancelFoundations, onConfirmFoundations }) {
  const progress = state.progress || {};
  const result = state.result;
  const preview = state.preview;
  const activeSeasons = (preview?.seasons || []).filter((season) => Number(season?.open_fixtures || 0) > 0);
  const quietSeasons = (preview?.seasons || []).filter((season) => Number(season?.open_fixtures || 0) === 0);
  const regularCount = preview?.safeTasks?.length || 0;
  const historicalCount = preview?.historicalGaps?.length || 0;
  const hasWork = regularCount + historicalCount > 0;
  const plannedCount = regularCount + historicalCount;
  const foundationSeasons = (preview?.seasons || []).filter(
    (season) => season?.foundations?.foundation_status === "reference_available"
  );
  const hasFoundationOpportunity = foundationSeasons.length > 0;
  const isStart = !state.running && !result && !preview;
  const completionKey = result?.workBlocked
    ? "help.myDataUpdate.workBlocked"
    : result?.updated > 0
    ? "help.myDataUpdate.completedWithUpdates"
    : result?.remainingOpen
    ? "help.myDataUpdate.completedWithResidual"
    : "help.myDataUpdate.allUpToDate";

  return (
    <Dialog open={state.open} onClose={state.running || state.foundationRunning ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{i18next.t(result && !result.cancelled ? "help.myDataUpdate.completedTitle" : "help.myDataUpdate.title")}</DialogTitle>
      <DialogContent dividers>
        {isStart && (
          <Typography variant="body2" sx={{ lineHeight: 1.65, whiteSpace: "pre-line" }}>
            {i18next.t("help.myDataUpdate.startIntroduction")}
          </Typography>
        )}

        {state.running && (
          <Box role="status" aria-live="polite" sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>
              {progress.phase === "scan" ? i18next.t("help.myDataUpdate.scanning") : i18next.t("help.myDataUpdate.running")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {progress.leagueName || ""} · {progress.current || 0}/{progress.total || 0}
            </Typography>
            <LinearProgress variant={progress.total ? "determinate" : "indeterminate"} value={progress.total ? Math.min(100, (progress.current / progress.total) * 100) : 0} />
          </Box>
        )}

        {state.foundationRunning && (
          <Box role="status" aria-live="polite" sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>{i18next.t("help.myDataUpdate.foundationsRunning")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {progress.leagueName || ""} · {progress.current || 0}/{progress.total || 0}
            </Typography>
            <LinearProgress variant={progress.total ? "determinate" : "indeterminate"} value={progress.total ? Math.min(100, (progress.current / progress.total) * 100) : 0} />
          </Box>
        )}

        {result && (
          <Box role={result.success ? "status" : "alert"} sx={{ mt: 2 }}>
            <Typography>
              {result.success
                ? i18next.t(result.cancelled ? "help.myDataUpdate.cancelledSummary" : completionKey, { updated: result.updated || 0 })
                : i18next.t("help.myDataUpdate.failed", { updated: result.updated, message: result.message })}
            </Typography>
            {result.cancelled && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {i18next.t("help.myDataUpdate.cancelledExplanation")}
              </Typography>
            )}
          </Box>
        )}

        {!state.running && !state.foundationRunning && !result && preview && (
          <Stack spacing={1.75} sx={{ mt: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{i18next.t("help.myDataUpdate.foundTitle")}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {hasWork
                  ? i18next.t("help.myDataUpdate.foundSummary", { leagues: activeSeasons.length, regular: regularCount, historical: historicalCount })
                  : hasFoundationOpportunity
                  ? i18next.t("help.myDataUpdate.foundationsOnlySummary", { count: foundationSeasons.length })
                  : i18next.t("help.myDataUpdate.cleanSummary")}
              </Typography>
              {hasWork && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
                  {i18next.t("help.myDataUpdate.plannedCount", { count: plannedCount })}
                </Typography>
              )}
              {plannedCount >= 100 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {i18next.t("help.myDataUpdate.longRunningWarning")}
                </Typography>
              )}
              {hasWork && historicalCount === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {i18next.t("help.myDataUpdate.regularOnly")}
                </Typography>
              )}
            </Box>

            {hasFoundationOpportunity && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography sx={{ fontWeight: 700 }}>{i18next.t("help.myDataUpdate.foundationsTitle")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {i18next.t("help.myDataUpdate.foundationsAvailable", { count: foundationSeasons.length })}
                </Typography>
                {!state.foundationConfirmation ? (
                  <Button size="small" sx={{ mt: 1 }} onClick={onRequestFoundations}>
                    {i18next.t("help.myDataUpdate.addFoundations")}
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" onClick={onCancelFoundations}>{i18next.t("help.myDataUpdate.cancelFoundationAddition")}</Button>
                    <Button size="small" variant="contained" onClick={onConfirmFoundations}>{i18next.t("help.myDataUpdate.confirmFoundationAddition")}</Button>
                  </Stack>
                )}
              </Paper>
            )}

            {historicalCount > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography sx={{ fontWeight: 700 }}>{i18next.t("help.myDataUpdate.historicalTitle")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
                  {i18next.t("help.myDataUpdate.historicalDecisionExplanation")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1.25, fontWeight: 700 }}>
                  {i18next.t("help.myDataUpdate.modelDecision")}
                </Typography>
                <RadioGroup value={state.evaluateHistorical === null ? "" : String(state.evaluateHistorical)} onChange={(event) => onDecisionChange(event.target.value === "true")} sx={{ gap: 1, mt: 1 }}>
                  <FormControlLabel value="true" control={<Radio />} sx={{ alignItems: "flex-start", border: 1, borderColor: "divider", borderRadius: 1, m: 0, px: 1, py: 0.75, width: "100%", cursor: "pointer", ".MuiFormControlLabel-label": { flex: 1 } }} label={(
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{i18next.t("help.myDataUpdate.modelDecisionYes")}</Typography>
                      <Typography variant="caption" color="text.secondary">{i18next.t("help.myDataUpdate.modelDecisionYesDescription")}</Typography>
                    </Box>
                  )} />
                  <FormControlLabel value="false" control={<Radio />} sx={{ alignItems: "flex-start", border: 1, borderColor: "divider", borderRadius: 1, m: 0, px: 1, py: 0.75, width: "100%", cursor: "pointer", ".MuiFormControlLabel-label": { flex: 1 } }} label={(
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{i18next.t("help.myDataUpdate.modelDecisionNo")}</Typography>
                      <Typography variant="caption" color="text.secondary">{i18next.t("help.myDataUpdate.modelDecisionNoDescription")}</Typography>
                    </Box>
                  )} />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">{i18next.t("help.myDataUpdate.noStillComplete")}</Typography>
              </Paper>
            )}

            {(activeSeasons.length > 0 || quietSeasons.length > 0) && (
              <Box component="details">
                <Typography component="summary" variant="body2" sx={{ cursor: "pointer", fontWeight: 700 }}>
                  {i18next.t("help.myDataUpdate.showLeagueDetails")}
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {[...activeSeasons, ...quietSeasons].map((season) => (
                    <Paper variant="outlined" sx={{ p: 1.25 }} key={`${season.league_id}:${season.season_id}`}>
                      <Typography sx={{ fontWeight: 700 }}>{season.league_name} · {season.season_name}</Typography>
                      <Typography variant="body2">{i18next.t("help.myDataUpdate.leagueFixtureCounts", { total: season.final_fixtures || 0, complete: season.finalized_fixtures || 0, open: season.open_fixtures || 0 })}</Typography>
                      <Typography variant="body2" color="text.secondary">{i18next.t("help.myDataUpdate.leagueCompileCounts", { compiled: season.compiled_open_fixtures || 0, uncompiled: season.uncompiled_fixtures || 0 })}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {i18next.t("help.myDataUpdate.leagueChronology", { regular: season.safe_tasks?.length || 0, historical: season.historical_gaps?.length || 0 })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {i18next.t("help.myDataUpdate.foundationDetails", {
                          parameters: season.foundations?.season_parameters_valid ? i18next.t("help.myDataUpdate.yes") : i18next.t("help.myDataUpdate.no"),
                          strengths: season.foundations?.team_strengths_valid ? i18next.t("help.myDataUpdate.yes") : i18next.t("help.myDataUpdate.no"),
                          reference: season.foundations?.reference_available ? i18next.t("help.myDataUpdate.yes") : i18next.t("help.myDataUpdate.no"),
                        })}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {state.running ? (
          <Button onClick={onCancel} disabled={state.stopping}>
            {i18next.t(state.stopping
              ? "help.myDataUpdate.stopping"
              : "help.myDataUpdate.cancelRun")}
          </Button>
        ) : !state.foundationRunning && (
          <Button onClick={onClose}>{i18next.t("help.myDataUpdate.close")}</Button>
        )}
        {!state.running && !state.foundationRunning && !result && preview && hasWork && (
          <Button variant="contained" onClick={onStart} disabled={historicalCount > 0 && state.evaluateHistorical === null}>
            {i18next.t("help.myDataUpdate.startNow")}
          </Button>
        )}
        {isStart && !state.foundationRunning && (
          <Button variant="contained" onClick={onCheck}>
            {i18next.t("help.myDataUpdate.checkData")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
